import React from 'react';
import { RegistrationItem, RegistrationItemVariation } from '../../../models/registrations/registrations';
import { formatCurrencyValue } from '../../../utils/currency';
import { LineItem } from '../../LineItems/LineItem';
import { ForSaleConfiguration, LineItemTypes, ShirtConfiguration, ShirtData } from '../../LineItems/LineItemTypes/LineItemTypes';
import { fixedPriceConfiguration, LineItemUnitPriceTypes } from '../../LineItems/UnitPriceTypes/LineItemUnitPriceTypes';
import { getItemVariationsForItem } from '../../../functions/Items';

interface ShirtItemPanelProps {
    registrationItem: RegistrationItem,
    currentTime: moment.Moment,
    adjustGrandTotal: (arg0: number) => void,
    adjustCartItem: (id: number, cart: LineItem) => void,
    disableInputs: boolean
}


const ShirtItemPanel = (props: ShirtItemPanelProps) => {

    const [quantityOrdered, setQuantityOrdered] = React.useState<number>(0);
    const [variations, setVariations] = React.useState<RegistrationItemVariation[]>([]);

    const currency = {
        prefix: '$',
        suffix: '',
        decimalSeparator: '.',
        groupSeparator: ',',
        decimalLimit: 2
    }

    const unitPrice = props.registrationItem.unitPrice ? formatCurrencyValue(props.registrationItem.unitPrice, currency) : undefined;

    const unitPriceFormatted = unitPrice ? unitPrice.formatted : undefined;

    const isInventoryInfinite = props.registrationItem.remainingInventory === null;

    const isInventoryAvailable = props.registrationItem.remainingInventory != 0;



    React.useEffect(() => 
    {
        getItemVariationsForItem(props.registrationItem.id).then((variants) => 
        {
            setVariations(variants);
        });
    },[]);

    React.useEffect(() => 
    {

        const unitPriceTemplate = 
        {
            currency: {
                name: 'USD',

                localization: Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD'
                }),

                formatting: {
                    prefix: '$',
                    suffix: '',
                    decimalSeparator: '.',
                    groupSeparator: ',',
                    decimalLimit: 2
                }
            },

            value: {}
        }


        const configurationVariants = variations.map((variant) =>
        {
            const unitPriceVar = variant.unitPrice ? formatCurrencyValue(variant.unitPrice,unitPriceTemplate.currency.formatting)
                : { float: 0, formatted: "$0.00", string: "0.00"}

            return {
                dbId: variant.id,
                displayName: variant.displayName,
                maxInventory: props.registrationItem.remainingInventory,
                unitPrice: {
                    ...unitPriceTemplate,
                    value: unitPriceVar
                }
            };
        });

        const dataVariants = variations.map((variant) =>
        {
            const unitPriceVar = variant.unitPrice ? formatCurrencyValue(variant.unitPrice,unitPriceTemplate.currency.formatting)
                : { float: 0, formatted: "$0.00", string: "0.00"}

            return {
                    dbId: variant.id,
                    displayName: variant.displayName,
                    quantity: 0,

                    unitPrice: {
                        ...unitPriceTemplate,
                        value: unitPriceVar
                    }
            };
        });

        const cartItem: LineItem = 
        {
            itemFormat: LineItemTypes.Shirts,

            itemConfiguration: 
            {
                dbId: props.registrationItem.id,
                componentName:'LineItemShirtData',
                totalQuantity: quantityOrdered,
                sizeConfiguration:configurationVariants,
            } as ShirtConfiguration,

            data:{
                totalQuantity: quantityOrdered,
                sizes: dataVariants
            } as ShirtData,

            name: props.registrationItem.displayName,
            displayOrd: props.registrationItem.displayOrd ?? 0,
            unitPriceFormat: LineItemUnitPriceTypes.FixedPrice,
            defaultPriceDisplay: unitPriceFormatted,

            unitPriceConfiguration: 
            {
                componentName: 'LineItemData',

                defaultPrice: 
                {

                    currency: 
                    {
                        name: 'USD',
                        localization: Intl.NumberFormat('en-US', 
                        {
                            style: 'currency',
                            currency: 'USD'
                        }),

                        formatting: 
                        {
                            prefix: '$',
                            suffix: '',
                            decimalSeparator: '.',
                            groupSeparator: ',',
                            decimalLimit: 2
                        }
                    },

                    value: props.registrationItem.unitPrice ? formatCurrencyValue(props.registrationItem.unitPrice * quantityOrdered, currency) : 0
                },

                price: 
                {
                    currency: 
                    {
                        name: 'USD',
                        localization: Intl.NumberFormat('en-US', 
                        {
                            style: 'currency',
                            currency: 'USD'
                        }),

                        formatting: 
                        {
                            prefix: '$',
                            suffix: '',
                            decimalSeparator: '.',
                            groupSeparator: ',',
                            decimalLimit: 2
                        }
                    },

                    value: unitPrice
                }
            } as fixedPriceConfiguration
        };

        props.adjustCartItem(props.registrationItem.id, cartItem);
    }, [quantityOrdered, variations]);  



    const isOrderingEnabled = () => 
    {
        if (props.disableInputs)
        {
            return {
                incrementerEnabled: false,
                decrementerEneabled: false
            }
        }
        if (isInventoryInfinite)
        {
            return {
                incrementerEnabled: true,
                decrementerEneabled: true
            }
        }
        if (isInventoryAvailable && props.registrationItem.remainingInventory)  
        {
            return {
                incrementerEnabled: quantityOrdered < props.registrationItem.remainingInventory,
                decrementerEnabled: quantityOrdered > 0
            }
        } 
        else 
        {
            return {
                incrementerEnabled: false,
                decrementerEnabled: false
            }
        }
    }



    const inventoryDisplay = () =>
    {
        if (isInventoryInfinite)
        {
            return <div></div>
        }
        if (isInventoryAvailable)
        {
            return <i> {props.registrationItem.remainingInventory} remaining</i>
        }
        return <i>Sold out</i>
    }



    return(
        <div className='colPanel-lineItem'>
            <div className='leftAlign'>
                <b>{props.registrationItem.displayName}</b>
            </div>
            <div className='colPanel-buttonGroup'>

                <button
                    className='colPanel-button'
                    disabled={!isOrderingEnabled().decrementerEnabled}
                    onClick={() => {
                        setQuantityOrdered(quantityOrdered-1);
                        props.registrationItem.unitPrice && props.adjustGrandTotal(props.registrationItem.unitPrice * -1);
                    }}>-</button>
                <b className='colPanel-inline'>{quantityOrdered}</b>
                <button
                    className='colPanel-button'
                    disabled={!isOrderingEnabled().incrementerEnabled}
                    onClick={() => {
                        setQuantityOrdered(quantityOrdered+1);
                        props.registrationItem.unitPrice && props.adjustGrandTotal(props.registrationItem.unitPrice);
                    }}>+</button>

            </div>
            <div className='leftAlign'>
                <span>{unitPriceFormatted}</span>
            </div>
            <div className='rightAlign'>
                {inventoryDisplay()}
                <br></br>
            </div>
        </div>
    )
}

export default ShirtItemPanel;